import { useState, useCallback, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Input, FormField, GstinInput, Select, useToast } from '@bbpos/ui';
import { locationService } from '@/services/location.service';
import { useTaxRegime } from '@/hooks/use-tax-regime';
import { STATE_OPTIONS } from '@/constants/indian-states';
import { PHONE_RE } from '@/utils/validation';
import type { Location, UpdateLocationPayload } from '@/types/location';
import { useTenantDial } from '@/hooks/use-tenant-dial';
import { useTenantCountry } from '@/hooks/use-tenant-country';
import { addressFieldsFor, clampPostalInput, isValidPostalCode } from '@/utils/address-fields';

// ─── Design tokens ──────────────────────────────────────────────────────────
const GLASS_PANEL =
  'rounded-2xl bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)] dark:shadow-[0_12px_30px_-18px_rgba(0,0,0,0.5)]';

interface LocationDetailTabProps {
  location: Location;
}

export function LocationDetailTab({ location }: LocationDetailTabProps) {
  const dial = useTenantDial();
  const country = useTenantCountry();
  const addressFields = addressFieldsFor(country, STATE_OPTIONS);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const tax = useTaxRegime();

  const [name, setName] = useState(location.name);
  const [address, setAddress] = useState(location.address ?? '');
  const [city, setCity] = useState(location.city ?? '');
  const [state, setState] = useState(location.state ?? '');
  const [pincode, setPincode] = useState(location.pincode ?? '');
  const [phone, setPhone] = useState(location.phone ?? '');
  const [gstin, setGstin] = useState(location.gstin ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when location prop changes
  useEffect(() => {
    setName(location.name);
    setAddress(location.address ?? '');
    setCity(location.city ?? '');
    setState(location.state ?? '');
    setPincode(location.pincode ?? '');
    setPhone(location.phone ?? '');
    setGstin(location.gstin ?? '');
  }, [location]);

  const updateMutation = useMutation({
    mutationFn: (payload: UpdateLocationPayload) =>
      locationService.update(location.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['location', location.id] });
      toast({ title: 'Location updated successfully', variant: 'success' });
    },
    onError: (err: Error) => {
      toast({ title: err.message ?? 'Failed to update location', variant: 'error' });
    },
  });

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!name || name.length < 2) {
      errs.name = 'Location name is required (min 2 characters)';
    }
    if (!isValidPostalCode(pincode, country.iso)) {
      errs.pincode = `${addressFields.postalLabel} is not valid for ${country.name}`;
    }
    if (phone && !PHONE_RE.test(phone)) {
      errs.phone = 'Enter a valid phone number';
    }
    if (gstin && tax.taxIdRegex && !tax.taxIdRegex.test(gstin)) {
      errs.gstin = `Invalid ${tax.taxIdLabel} format`;
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    updateMutation.mutate({
      name: name.trim(),
      address: address || undefined,
      city: city || undefined,
      state: state || undefined,
      pincode: pincode || undefined,
      phone: phone || undefined,
      gstin: gstin || undefined,
    });
  }

  // Ctrl+S → Save (spec 3.12)
  const handleSave = useCallback(() => {
    if (!validate()) return;
    updateMutation.mutate({
      name: name.trim(),
      address: address || undefined,
      city: city || undefined,
      state: state || undefined,
      pincode: pincode || undefined,
      phone: phone || undefined,
      gstin: gstin || undefined,
    });
  }, [name, address, city, state, pincode, phone, gstin]);

  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleSave]);

  return (
    <form onSubmit={handleSubmit} className="animate-slide-up">
      <div className={`${GLASS_PANEL} p-6`}>
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500 mb-4">
          Location Information
        </h3>

        <div className="space-y-4">
          <FormField label="Location Name" htmlFor="edit-name" required error={errors.name}>
            <Input
              id="edit-name"
              value={name}
              onChange={(v) => setName(v)}
            />
          </FormField>

          <FormField label="Location Code" htmlFor="edit-code">
            <Input
              id="edit-code"
              value={location.location_code}
              disabled
            />
            <p className="mt-1 text-xs text-neutral-400 dark:text-neutral-500">
              Location code cannot be changed after creation.
            </p>
          </FormField>

          <FormField label="Address" htmlFor="edit-address">
            <Input
              id="edit-address"
              value={address}
              onChange={(v) => setAddress(v)}
            />
          </FormField>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="City" htmlFor="edit-city">
              <Input
                id="edit-city"
                value={city}
                onChange={(v) => setCity(v)}
              />
            </FormField>

            <FormField label={addressFields.regionLabel} htmlFor="edit-state">
              <Select
                id="edit-state"
                options={addressFields.regionOptions}
                value={state}
                onChange={(v) => setState(typeof v === 'string' ? v : '')}
                placeholder={addressFields.regionPlaceholder}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label={addressFields.postalLabel} htmlFor="edit-pincode" error={errors.pincode}>
              <Input
                id="edit-pincode"
                placeholder={addressFields.postalPlaceholder}
                value={pincode}
                onChange={(v) => setPincode(clampPostalInput(v, addressFields))}
              />
            </FormField>

            <FormField label="Phone" htmlFor="edit-phone" error={errors.phone}>
              <Input
                id="edit-phone"
                variant="phone"
                defaultDial={dial}
                placeholder="98765 43210"
                value={phone}
                onChange={(v) => setPhone(v)}
              />
            </FormField>
          </div>
        </div>

        <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500 mt-8 mb-4">
          Tax & Compliance
        </h3>

        <div className="space-y-4">
          <GstinInput
            regime={tax}
            value={gstin}
            onChange={setGstin}
            error={errors.gstin}
          />
        </div>

        <div className="flex items-center justify-end mt-8 pt-4">
          <Button
            variant="primary"
            type="submit"
            loading={updateMutation.isPending}
          >
            Save Changes
          </Button>
        </div>

        <p className="text-xs text-neutral-400 dark:text-neutral-500 text-right mt-1">
          Ctrl+S to save
        </p>
      </div>
    </form>
  );
}
